import { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import { useSnackbar } from 'notistack';

import type { PDFDocumentProxy } from 'pdfjs-dist';

interface Params {
  readonly url: string;
  readonly withCredentials?: boolean;
}

export const usePdf = ({ url, withCredentials }: Readonly<Params>) => {
  const [isLoading, setIsLoading] = useState(true);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const pdfRef = useRef<PDFDocumentProxy | null>(null);

  useEffect(() => {
    const loadingTask = pdfjs.getDocument({ url, withCredentials });

    loadingTask.promise
      .then(
        async (pdf) => {
          // eslint-disable-next-line functional/immutable-data
          pdfRef.current = pdf;

          setTotalPageCount(pdf.numPages);

          // Fetch the first page
          await pdf.getPage(1);

          setIsLoading(false);
        },
        () => {
          enqueueSnackbar('Impossible de charger le PDF.', {
            variant: 'error',
          });

          setIsLoading(false);
        },
      )
      .catch((error: unknown) => {
        enqueueSnackbar('Impossible de charger le PDF.', {
          variant: 'error',
        });

        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [enqueueSnackbar, setIsLoading, url, withCredentials]);

  const getPage = useCallback(
    (index: number) =>
      pdfRef.current ? pdfRef.current.getPage(index + 1) : undefined,
    [],
  );

  return {
    getPage,
    isLoading,
    totalPageCount,
  };
};
